import { BrandfitManager } from '@/ui/brandfit/BrandfitManager';
import { createReactiveState, type ReactiveState } from '@/utils/reactive-store';

import type { ConfigurationSetup } from '../config/config-setup.types';
import { ApiError } from '../errors/ApiError';
import type { AnalysisRequest, AnalysisResultData, TextBlock } from '../TeoApi';
import type { TeoCore } from '../TeoCore';
import type { TextData } from '../TeoStorage';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import { AIHandler } from './AIHandler';
import { AnalysisResult } from './AnalysisResult';
import { DiffMarkers } from './DiffMarkers';
import { GeneratorUiHandler, type GeneratorUiHandlerStoredState } from './GeneratorUiHandler';
import type { GeneratorStateExported } from './GeneratorUIState';
import type { HistoryEntry } from './history/HistoryEntry';
import { HistoryManager } from './history/HistoryManager';
import { MarkerManager } from './MarkerManager';
import { SpellCheckHandler } from './SpellCheckHandler';
import { StyleHandler } from './StyleHandler';
import { TextUi, type TeoView, type TextUiState } from './TextUi';
import { WordListHandler } from './WordListHandler/WordListHandler';

export interface TextState {
  view: TeoView;
}

export type TextEvents = {
  configChanged: (detail: AnalysisConfiguration | null) => void;
  textChanged: (detail: string) => void;
  textChangedExternal: () => void;
  gotAnalysis: (detail: AnalysisResult) => void;
  requestedAnalysis: (detail: AnalysisRequest) => void;
  analysisReadinessChanged: (detail: boolean) => void;
  analysisStateChanged: () => void;
  editable(detail: boolean): void;
  analysisPending: (detail: boolean) => void;
  historyChanged: (detail: HistoryEntry[]) => void;
};

export type AnalysisConfiguration = Record<
  'audience' | 'strategy' | 'medium' | 'is_informal',
  number
>;

export type AnalysisState = {
  isCurrent: boolean;
  canAnalyze: boolean;
  analysisPending: boolean;
  brandFitUpToDate: boolean;
  dictionaryUpToDate: boolean;
};

export interface TeoTextProps {
  core: TeoCore;
  id: number;
  text: string;
  serialized?: string;
  configuration?: Partial<AnalysisConfiguration>;
  lastEdited?: number;
  uiState?: Partial<TextUiState>;
  options?: {
    shouldStoreAfterCreate?: boolean;
    isTemporary?: boolean;
  };
  generatorUiState?: GeneratorUiHandlerStoredState;
}

export class TeoText {
  #state: ReactiveState<TextState>;
  brandfit = new BrandfitManager(this);
  id: number;
  text: string = '';
  serialized: string | undefined;
  analysis: AnalysisResult | null = null;
  events = new TeoEventTarget<TextEvents>();
  core: TeoCore;
  ui: TextUi;
  getTextArray: (() => TextBlock[]) | null = null;
  ai: AIHandler;
  wordLists: WordListHandler;
  spellCheck: SpellCheckHandler;
  style: StyleHandler;
  generatorUi: GeneratorUiHandler;
  markers: MarkerManager;
  diff: DiffMarkers;
  analysisPending = false;
  editorLoadingMessages: string[] = [];
  isTemporary = false;
  private _isEditable = true;
  history: HistoryManager;
  private _configuration?: Partial<AnalysisConfiguration>;
  private _readyToAnalyze = false;
  private listeners: Array<() => void> = [];
  private _disposed = false;

  clone(): TeoText {
    return new TeoText(
      this.core,
      this.id,
      this.text,
      this.serialized,
      { ...this._configuration },
      this.lastEdited,
      this.ui.state,
      { isTemporary: this.isTemporary },
      this.generatorUi?.toJSON(),
    );
  }

  static make(props: TeoTextProps) {
    return new TeoText(
      props.core,
      props.id,
      props.text ?? '',
      props.serialized,
      props.configuration,
      props.lastEdited ?? Date.now(),
      props.uiState,
      props.options,
      props.generatorUiState,
    );
  }

  constructor(
    core: TeoCore,
    id: number,
    text: string = '',
    serialized: string | undefined = undefined,
    configuration: Partial<AnalysisConfiguration> | undefined = undefined,
    public lastEdited: number = Date.now(),
    uiState: Partial<TextUiState> = {
      view: core.dynamicConfig.options.textGeneratorEnabled ? 'new' : 'performance',
    },
    options?: {
      shouldStoreAfterCreate?: boolean;
      isTemporary?: boolean;
    },
    generatorUiState: GeneratorUiHandlerStoredState | undefined = undefined,
  ) {
    const { shouldStoreAfterCreate = true, isTemporary = false } = options ?? {};
    this.text = text;
    this.serialized = serialized;
    this._configuration = configuration;
    this.id = id;
    this.core = core;
    this.#state = createReactiveState<TextState>({
      view:
        uiState.view ?? (core.dynamicConfig.options.textGeneratorEnabled ? 'new' : 'performance'),
    });
    this.ui = new TextUi({
      title: `Text ${id + 1}`,
      ...uiState,
    });
    this.isTemporary = isTemporary;
    this.history = new HistoryManager(this);
    this.markers = new MarkerManager(this);
    this.ai = new AIHandler(this);
    this.wordLists = new WordListHandler(this);
    this.spellCheck = new SpellCheckHandler(this);
    this.style = new StyleHandler(this);
    this.generatorUi = new GeneratorUiHandler(
      this,
      generatorUiState as GeneratorStateExported | undefined,
    );

    this.diff = new DiffMarkers(this);

    this.events.addEventListener('analysisPending', (pending) => {
      this.analysisPending = pending;
      if (!this.history.viewIsActive) {
        // while history is active, the text is not editable
        this.isEditable = !pending;
      }
      if (!pending) {
        this.editorLoadingMessages = [];
      }
      this.events.triggerEvent('analysisStateChanged', void 0);
    });
    this.ui.events.addEventListener('textProperty', () => this.markers.syncPerformanceMarkers());
    this.ui.events.addEventListener('view', (viewName: TeoView) => {
      if (viewName === 'performance') this.markers.syncPerformanceMarkers();
      // if (viewName === 'brand-scanner') this.syncAnalysis();
      if (this.#state.view !== viewName) {
        this.#state.view = viewName;
      }
    });
    this.ui.events.addEventListener('changed', () => {
      this.store();
    });
    if (shouldStoreAfterCreate) {
      this.store();
    }
    this.listeners.push(
      this.core.dynamicConfig.addEventListener('textConfigOptions', () => {
        const oldSetup = this.core.dynamicConfig.getOldValue('textConfigOptions');
        const newSetup = this.core.dynamicConfig.options.textConfigOptions;
        if (oldSetup && newSetup && this._configuration) {
          this.configuration = this.translateToNewConfiguration(
            this._configuration,
            oldSetup,
            newSetup,
          );
        } else {
          this.configuration = { ...(this._configuration ?? {}) };
        }
      }),
    );
  }

  get state(): ReactiveState<TextState> {
    return this.#state;
  }

  set configuration(config: Partial<AnalysisConfiguration> | undefined) {
    // TODO: actually do a deep value comparison here
    if (this._configuration === config) return;
    this._configuration = config;
    this.analysisConfigChanged();
    this.events.triggerEvent('configChanged', config as AnalysisConfiguration);
    this.checkReadyToAnalyze();
    this.store();
  }

  get configuration(): Partial<AnalysisConfiguration> | undefined {
    return this._configuration;
  }

  set isEditable(editable: boolean) {
    if (editable === this._isEditable) return;
    this._isEditable = editable;
    this.events.triggerEvent('editable', editable);
  }

  get isEditable(): boolean {
    return this._isEditable;
  }

  get whiteListActive(): boolean {
    return this.wordLists.whiteList.isActive;
  }

  get disposed(): boolean {
    return this._disposed;
  }

  // syncAnalysis() {
  //   const state = this.analysisState;
  //   if (!state.isCurrent && state.canAnalyze) {
  //     this.analyze();
  //   }
  // }

  checkReadyToAnalyze() {
    const ready = this.configIsSet() && this.textIsSet();
    if (ready !== this._readyToAnalyze) {
      this._readyToAnalyze = ready;
      this.events.triggerEvent('analysisReadinessChanged', this._readyToAnalyze);
      this.events.triggerEvent('analysisStateChanged', void 0);
    }
    return ready;
  }

  getTextData(): TextData {
    return {
      id: this.id,
      text: this.text,
      serialized: this.serialized,
      configuration: this.configuration,
      analysis: this.analysisState.isCurrent ? this.analysis?.toJSON() : undefined, // TODO: find a way to maybe restore analysis independent of the text?
      history: this.history.toJSON(),
      title: this.ui.state.title,
      lastEdited: this.lastEdited,
      generatorUi: this.generatorUi?.toJSON(),
      uiState: {
        view: this.ui.state.view,
        title: this.ui.state.title,
        previousView: this.ui.state.previousView,
      },
    };
  }

  configIsSet(): boolean {
    return !!(
      this._configuration !== undefined &&
      this._configuration.audience &&
      this._configuration.strategy &&
      this._configuration.medium &&
      this._configuration.is_informal
    );
  }

  textIsSet(): boolean {
    return this.text.length > 0;
  }

  // async analyze() {
  //   const checkBrandFit =
  //     (this.core.dynamicConfig.options.alwaysCheckBrandFit ||
  //       this.ui.state.view == 'brand-scanner') &&
  //     this.core.dynamicConfig.options.brandFitEnabled;
  //   if (checkBrandFit) {
  //     this.editorLoadingMessages = this.wordLists.brandFit.loadingMessages;
  //   }
  //   this.events.triggerEvent('analysisPending', true);
  //   await Promise.all([
  //     this.sendAnalysisRequest(),
  //     checkBrandFit ? this.wordLists.brandFit.scan() : undefined,
  //     this.ui.state.view == 'dictionary' ? this.wordLists.dictionary.scan() : undefined,
  //     this.ui.state.view == 'spellcheck' ? this.spellCheck?.check() : undefined,
  //   ]);
  //   this.events.triggerEvent('analysisPending', false);
  //   this.events.triggerEvent('analysisStateChanged', void 0);
  // }

  async analyze(options: { trigger?: AnalysisRequest['trigger'] }) {
    this.events.triggerEvent('analysisPending', true);
    await Promise.all([
      this.sendAnalysisRequest({ trigger: options.trigger }),
      this.ui.state.view == 'brand-scanner' || this.core.dynamicConfig.options.alwaysCheckBrandFit ?
        this.wordLists.brandFit.scan()
      : undefined,
      this.ui.state.view == 'dictionary' ? this.wordLists.dictionary.scan() : undefined,
      this.ui.state.view == 'spellcheck' ? this.spellCheck?.check() : undefined,
    ]);
    this.events.triggerEvent('analysisPending', false);
    this.events.triggerEvent('analysisStateChanged', void 0);
  }

  async sendAnalysisRequest({ trigger }: { trigger?: AnalysisRequest['trigger'] }) {
    if (!this.configIsSet()) {
      console.error('No configuration set for analysis');
      return;
    }
    const configuration = this._configuration as AnalysisConfiguration;
    let text: string | TextBlock[] = this.text;
    if (this.getTextArray !== null) {
      text = this.getTextArray();
    }
    const testRequest: AnalysisRequest = {
      text,
      ...configuration,
      use_whitelist: this.whiteListActive,
      trigger,
    };
    const lastInstruction = this.analysis?.currentInstruction;
    if (lastInstruction) {
      testRequest.last_instruction_id = lastInstruction.id;
      testRequest.last_text_property = lastInstruction.text_property_name;
    }
    if (this.core.dynamicConfig.options.useExpParser) {
      testRequest.use_exp_parser = true;
    }
    this.events.triggerEvent('requestedAnalysis', testRequest);

    const result = await this.core.api.getAnalysis(testRequest);

    if (ApiError.isError(result)) {
      this.core.notifications.add({
        title: 'Analyse fehlgeschlagen',
        message: `${result.error}`,
        mode: 'error',
      });
      console.error('Error in analysis result:', result);
      return;
    }

    this.updateResult(result, text);
    this.history.addHistoryEntry();

    if (result.usage_quota?.type === 'ANALYSIS') {
      this.core.updateUsageQuota(result.usage_quota);
    }
  }

  updateResult(result: AnalysisResultData, text: string | TextBlock[]) {
    this.analysis = new AnalysisResult(result, this, text);
    this.events.triggerEvent('analysisStateChanged', void 0);
    this.events.triggerEvent('gotAnalysis', this.analysis);
  }

  get analysisState(): AnalysisState {
    return {
      isCurrent: !!this.analysis?.createdAt && this.analysis.createdAt >= this.lastEdited,
      canAnalyze: this.configIsSet() && this.textIsSet(),
      analysisPending: this.analysisPending,
      brandFitUpToDate: this.wordLists.brandFit.isUpToDate,
      dictionaryUpToDate: this.wordLists.dictionary.isUpToDate,
    };
  }

  updateText(
    newText: string | undefined,
    serialized: string | undefined = undefined,
    isExternal: boolean = true,
  ) {
    if (newText === this.text) return;
    if (!this._isEditable) {
      console.error('Text changed while not editable');
      return;
    }
    // if (serialized !== undefined && serialized !== this.serialized) {
    this.serialized = serialized;
    // }
    if (newText !== undefined && newText !== this.text) {
      this.text = newText;
      if (isExternal) {
        this.events.triggerEvent('textChangedExternal', undefined);
      } else {
        this.events.triggerEvent('textChanged', this.text);
      }
    }
    this.lastEdited = Date.now();
    this.store();
    this.events.triggerEvent('analysisStateChanged', void 0);
    // this.events.triggerEvent('textChanged', this.text);
    this.checkReadyToAnalyze();
  }

  updateTitle(title: string) {
    this.ui.update('title', title);
    this.store();
  }

  updateTextArrayFunction(getTextArray: () => TextBlock[]) {
    this.getTextArray = getTextArray;
  }

  #toStoreDebounceTimeout: ReturnType<typeof setTimeout> | undefined;

  private storeDebounced() {
    if (this.#toStoreDebounceTimeout) {
      clearTimeout(this.#toStoreDebounceTimeout);
    }
    this.#toStoreDebounceTimeout = setTimeout(() => {
      this.core.storage.saveText(this.getTextData());
    }, 16);
  }

  store() {
    if (this.isTemporary || this._disposed) return; // Do not store temporary or disposed texts
    // small debounce
    this.storeDebounced();
  }

  touch(): void {
    this.lastEdited = Date.now();
    this.events.triggerEvent('analysisStateChanged', void 0);
  }

  // TODO make this private again and handle whitelist toggle more on the configuration side
  public analysisConfigChanged() {
    if (this.analysisState.isCurrent) {
      // TODO: implement something real here
      // it depends on if analysis should be stored too, or, or, or...
      this.lastEdited = Date.now();
      this.events.triggerEvent('analysisStateChanged', void 0);
    }
  }

  dispose() {
    this.listeners.forEach((listener) => listener());
    this.listeners = [];
    this._disposed = true;
  }

  /**
   * Matches the selected options in the config out of the old setup to options from the new setup by name
   * @param config The current analysis configuration
   * @param oldSetup The old configuration setup
   * @param newSetup The new configuration setup
   * @returns The translated analysis configuration
   */
  private translateToNewConfiguration(
    config: Partial<AnalysisConfiguration>,
    oldSetup: ConfigurationSetup,
    newSetup: ConfigurationSetup,
  ): Partial<AnalysisConfiguration> {
    const newConfig: Partial<AnalysisConfiguration> = {};

    for (const sectionName in config) {
      const oldSection = oldSetup.sections.find((s) => s.name === sectionName);
      const oldOptionName = oldSection?.options.find(
        (o) => o.id === config[sectionName as keyof AnalysisConfiguration],
      )?.name;
      if (!oldOptionName) continue;
      const newSection = newSetup.sections.find((s) => s.name === sectionName);
      const newOptionId = newSection?.options.find((o) => o.name === oldOptionName)?.id;
      if (newOptionId !== undefined) {
        newConfig[sectionName as keyof AnalysisConfiguration] = newOptionId;
      }
    }
    return newConfig;
  }
}
